/**
 * OPENING WHAT A NOTIFICATION POINTS AT — through the app's own opener, not past it.
 *
 * Owner, 2026-09-18, reproduced by avg-devapp-appstore in the simulator: tapping
 * a notification opened the app and changed the URL, and then showed the Agents
 * list anyway. The conversation WAS selected — underneath a rail that still
 * covered it.
 *
 * On a phone the rail IS the screen (`railCovers: phoneNav && sbOpen`), so every
 * in-app opener goes through `pushDetail()`, which closes it. The push path did
 * not: it wrote the URL and fired a synthetic popstate, useUrlState picked the
 * selection up, the relay retargeted — and nothing told the rail to get out of
 * the way. On a wide screen the rail is docked beside the stage, so the identical
 * code looks correct, which is presumably where it was tried.
 *
 * WHY A REGISTRY RATHER THAN A DIRECT CALL. The listener must be armed at BOOT,
 * because iOS delivers a cold-start tap immediately after launch — before React
 * has mounted and long before `pushDetail` exists. So boot-time code calls
 * `openPushPath`, and whether that lands on the app's real opener or on the URL
 * fallback depends only on whether the app has finished starting:
 *
 *   warm  App has registered  -> its opener runs, the rail gets out of the way
 *   cold  nothing registered  -> the URL is set, and App reads it at first render
 *
 * Both are correct; they are the same destination reached by the only two routes
 * available at those two moments.
 */

type Opener = (path: string) => void

let opener: Opener | null = null

/** App claims notification navigation for as long as it is mounted. */
export function registerPushOpener(fn: Opener): () => void {
  opener = fn
  return () => {
    if (opener === fn) opener = null
  }
}

/**
 * Go where a notification points.
 *
 * The fallback is not a lesser path — it is the only one that exists before the
 * app has rendered, and App's first render reads the URL it leaves behind.
 */
/**
 * The last path acted on, and when. See the dedupe in openPushPath.
 */
let lastPath = ''
let lastAt = 0

/**
 * How long the same path is treated as the same tap.
 *
 * Generous enough to cover two transports answering one tap milliseconds apart,
 * short enough that a person genuinely tapping the same agent's notification
 * twice still gets taken there the second time.
 */
const SAME_TAP_MS = 3_000

export function openPushPath(path: string): void {
  if (!path) return

  /*
   * ONE TAP, ONE NAVIGATION — and the case this exists for is the COLD one.
   *
   * Corrected 2026-09-18 after avg-devapp-appstore described what their shell
   * actually does, because the first version of this comment named the wrong
   * case and a guard with a false reason is a guard somebody deletes later:
   *
   *   WARM  their delegate forwards didReceive to Capacitor and dispatches NO
   *         event, so the plugin listener is the ONLY reporter. No double.
   *   COLD  the tap comes from the scene's connectionOptions and is handed to
   *         the page as `avengents:push-open`. But if iOS ALSO delivers
   *         didReceive after Capacitor's router has been wrapped, that is
   *         forwarded too and the plugin listener fires as well — two reports of
   *         one tap, same path, about a second apart.
   *
   * So neither transport is redundant: the listener is the warm path, the event
   * is the cold path and the Mac, and this is the overlap between them. Three
   * seconds is sized for that observed ~1s gap with room to spare, and stays far
   * short of a person deliberately tapping the same agent again.
   *
   * At the chokepoint, so every present and future transport inherits it.
   */
  const now = Date.now()
  if (path === lastPath && now - lastAt < SAME_TAP_MS) return
  lastPath = path
  lastAt = now

  if (opener) {
    opener(path)
    return
  }
  window.history.replaceState(null, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}
